import service,{BillSuggestion,CashInspection,CashOperation,CashWorkspaceData,CategoryReport,IncomeSuggestion} from '../../services/PaydayCashService';
// One authenticated account. Server results are never recomputed here. The last
// compact picture is kept on device so Overview can paint while a new read runs.
export type SaveOutcome={saved:true;updated:boolean;updating?:boolean};
type View={savedRefresh?:boolean;history?:CashInspection;historyAt?:number|null;historyLoading?:boolean;historyError?:boolean;billSuggestions?:BillSuggestion[];suggestionsLoading?:boolean;suggestionsError?:boolean;suggestionsAt?:number|null;incomeSuggestions?:IncomeSuggestion[];incomeLoading?:boolean;incomeError?:boolean;incomeAt?:number|null;uid?:string;data:CashWorkspaceData|null;loading:boolean;error:Error|null;receivedAt:number|null};
const empty:View={data:null,loading:true,error:null,receivedAt:null};
const PICTURE_KEY='finflow_picture_';
const CATEGORY_KEY='finflow_categories_';
const ACTIVITY_KEY='finflow_activity_';
let view:View=empty,epoch=0,flight:Promise<void>|null=null,busy=false;
let historyFlight:Promise<void>|null=null;
let suggestionFlight:Promise<void>|null=null;
let incomeFlight:Promise<void>|null=null;
const categoryReports=new Map<string,{at:number;request:Promise<CategoryReport>}>();
let expiry:ReturnType<typeof setTimeout>|undefined;
const listeners=new Set<()=>void>();
const publish=(next:View)=>{view=next;listeners.forEach(fn=>fn());};
function pictureKey(uid:string){return PICTURE_KEY+uid;}
function usablePicture(data:unknown):data is CashWorkspaceData{
 return !!data&&typeof data==='object'&&Array.isArray((data as CashWorkspaceData).issues)&&typeof (data as CashWorkspaceData).today==='string'&&!!(data as CashWorkspaceData).state&&['snapshots','plans','occurrences','links','events'].every(key=>Array.isArray((data as CashWorkspaceData).state[key as 'snapshots']));
}
function persistPicture(uid:string,data:CashWorkspaceData){
 if(data.detailsComplete!==false||!usablePicture(data))return;
 try{localStorage.setItem(pictureKey(uid),JSON.stringify({data}));}catch{/* Device storage is optional assistance. */}
}
function readPicture(uid:string){
 try{
  const parsed=JSON.parse(localStorage.getItem(pictureKey(uid))||'');
  return usablePicture(parsed?.data)?parsed.data as CashWorkspaceData:null;
 }catch{return null;}
}
function forgetPicture(uid?:string){
 try{
  const prefixes=uid?[PICTURE_KEY+uid,CATEGORY_KEY+uid+'_',ACTIVITY_KEY+uid+'_']:[PICTURE_KEY,CATEGORY_KEY,ACTIVITY_KEY];
  for(let i=localStorage.length-1;i>=0;i--){const key=localStorage.key(i);if(key&&prefixes.some(prefix=>key===prefix||key.startsWith(prefix)))localStorage.removeItem(key);}
 }catch{/* ignore */}
}
function usableCategoryReport(data:unknown,month:string):data is CategoryReport{
 return !!data&&typeof data==='object'&&(data as CategoryReport).month===month&&Array.isArray((data as CategoryReport).groups);
}
export function peekCategoryReport(uid:string,month:string){
 try{
  const parsed=JSON.parse(localStorage.getItem(CATEGORY_KEY+uid+'_'+month)||'');
  return usableCategoryReport(parsed,month)?parsed:null;
 }catch{return null;}
}
function dropWorkspaceMemory(notify=true){categoryReports.clear();epoch++;flight=null;suggestionFlight=null;incomeFlight=null;historyFlight=null;busy=false;clearTimeout(expiry);if(notify)publish(empty);}
export function clearWorkspace(){dropWorkspaceMemory();forgetPicture();}
export function workspaceIdentity(uid?:string){
 if(view.uid===uid)return;
 dropWorkspaceMemory(false);
 if(!uid){publish(empty);return;}
 const cached=readPicture(uid);
 publish(cached?{...empty,uid,data:cached,loading:true,error:null,receivedAt:null}:{...empty,uid});
}
export const workspaceSnapshot=(uid?:string)=>view.uid===uid?view:empty;
export function subscribeWorkspace(fn:()=>void){clearTimeout(expiry);listeners.add(fn);return()=>{listeners.delete(fn);if(!listeners.size)expiry=setTimeout(dropWorkspaceMemory,10*60_000);};}
export async function readWorkspace(uid:string,overview=false,force=false):Promise<void>{
 workspaceIdentity(uid);if(busy)return;
 if(flight){await flight;if(!overview&&view.uid===uid&&view.data?.detailsComplete===false)return readWorkspace(uid,false);return;}
 const completeEnough=overview||view.data?.detailsComplete!==false;
 if(!force&&!view.error&&view.data&&completeEnough&&view.receivedAt!==null&&Date.now()-view.receivedAt>=0&&Date.now()-view.receivedAt<5*60_000)return;
 const generation=epoch;
 publish({...view,loading:true,error:null});
 const task=(async()=>{try{
  const data=await (overview?service.workspace(uid,{view:'overview'}):service.workspace(uid));
  if(generation===epoch&&view.uid===uid){persistPicture(uid,data);publish({...view,uid,data,loading:false,error:null,savedRefresh:false,receivedAt:Date.now()});}
 }catch(e){if(generation===epoch&&view.uid===uid)publish({...view,data:(e as any)?.code==='account_unavailable'?null:view.data,loading:false,error:e instanceof Error?e:new Error('Could not refresh your picture')});}
 })();flight=task;try{await task;}finally{if(flight===task)flight=null;}
}
export async function writeWorkspace(uid:string,operation:CashOperation,input:Record<string,unknown>,overview=false,backgroundRefresh=false){
 if(view.uid!==uid||busy)throw Error('Your account changed or a save is already in progress');
 busy=true;const generation=++epoch;flight=null;suggestionFlight=null;incomeFlight=null;historyFlight=null;
 // Keep the current picture on screen while the save request is in flight.
 try{await service.execute(operation,{...input,uid});
  if(epoch!==generation||view.uid!==uid)return;
  busy=false;
  if(backgroundRefresh){publish({...view,savedRefresh:true,receivedAt:null,billSuggestions:undefined,suggestionsAt:null,incomeSuggestions:undefined,incomeAt:null});void readWorkspace(uid,overview,true);return {saved:true,updated:false,updating:true} as SaveOutcome;}
  await readWorkspace(uid,overview,true);
  return {saved:true,updated:epoch===generation&&view.uid===uid&&!view.error&&!!view.data} as SaveOutcome;
 }catch(e){if(epoch===generation&&view.uid===uid){busy=false;publish({...view,loading:false,error:e instanceof Error?e:new Error('Save failed'),receivedAt:null});}throw e;}
 finally{if(epoch===generation)busy=false;}
}
export function invalidateWorkspace(){
 if(!view.uid||busy)return;epoch++;flight=null;suggestionFlight=null;incomeFlight=null;historyFlight=null;publish({...view,receivedAt:null,billSuggestions:undefined,suggestionsAt:null,incomeSuggestions:undefined,incomeAt:null,history:undefined,historyAt:null,historyLoading:false,historyError:false});
 if(listeners.size)void readWorkspace(view.uid,view.data?.detailsComplete!==true,true);
}
// Events can arrive while Account is mounted and no cash hook is subscribed.
if(typeof window!=='undefined'){
 window.addEventListener('finflow-bank-sync-complete',invalidateWorkspace);
 window.addEventListener('finflow-app-resumed',()=>{if(view.uid)void readWorkspace(view.uid,view.data?.detailsComplete!==true);});
}

// Optional assistance has its own loading state: it must not lock the cash UI.
export async function readBillSuggestions(uid:string,force=false){
 if(view.uid!==uid||busy||suggestionFlight)return;
 if(!force&&view.suggestionsAt!=null&&Date.now()-view.suggestionsAt<5*60_000)return;
 const generation=epoch;
 publish({...view,suggestionsLoading:true,suggestionsError:false});
 const task=(async()=>{try{
  const response=await service.suggestBills(uid);
  if(!Array.isArray(response.suggestions))throw Error('Suggestions unavailable');
  if(epoch===generation&&view.uid===uid)publish({...view,billSuggestions:response.suggestions as BillSuggestion[],suggestionsLoading:false,suggestionsAt:Date.now()});
 }catch{if(epoch===generation&&view.uid===uid)publish({...view,suggestionsLoading:false,suggestionsError:true,suggestionsAt:Date.now()});}})();
 suggestionFlight=task;try{await task;}finally{if(suggestionFlight===task)suggestionFlight=null;}
}

export async function readIncomeSuggestions(uid:string,force=false){
 if(view.uid!==uid||busy||incomeFlight)return;
 if(!force&&view.incomeAt!=null&&Date.now()-view.incomeAt<5*60_000)return;
 const generation=epoch;
 publish({...view,incomeLoading:true,incomeError:false});
 const task=(async()=>{try{
  const response=await service.suggestIncome(uid);
  if(!Array.isArray(response.suggestions))throw Error('Paycheck suggestions unavailable');
  if(epoch===generation&&view.uid===uid)publish({...view,incomeSuggestions:response.suggestions as IncomeSuggestion[],incomeLoading:false,incomeAt:Date.now()});
 }catch{if(epoch===generation&&view.uid===uid)publish({...view,incomeLoading:false,incomeError:true,incomeAt:Date.now()});}})();
 incomeFlight=task;try{await task;}finally{if(incomeFlight===task)incomeFlight=null;}
}

// Editing evidence has a separate cache. Compact cash responses cannot erase it.
export async function readPaymentHistory(uid:string,force=false){
 workspaceIdentity(uid);if(busy)return;
 if(historyFlight)return historyFlight;
 if(!force&&view.history&&view.historyAt!=null&&Date.now()-view.historyAt<5*60_000)return;
 const generation=epoch;publish({...view,historyLoading:true,historyError:false});
 const task=(async()=>{try{
  const data=await service.execute('inspect',{uid}) as unknown as CashInspection;
  if(!Array.isArray(data?.events)||!Array.isArray(data?.snapshots))throw Error('Payment history unavailable');
  if(epoch===generation&&view.uid===uid)publish({...view,history:data,historyAt:Date.now(),historyLoading:false,historyError:false});
 }catch{if(epoch===generation&&view.uid===uid)publish({...view,historyLoading:false,historyError:true});}})();
 historyFlight=task;try{await task;}finally{if(historyFlight===task)historyFlight=null;}
}

// Cache server totals by account, month and evidence revision; never compute them locally.
export async function readCategoryReport(uid:string,month:string,evidenceKey:string,force=false):Promise<CategoryReport>{
 workspaceIdentity(uid);
 const key=JSON.stringify([uid,month,evidenceKey]),cached=categoryReports.get(key);
 if(!force&&cached&&Date.now()-cached.at>=0&&Date.now()-cached.at<5*60_000)return cached.request;
 const request=service.execute('spending-categories',{uid,month}).then(value=>{
  if(!Array.isArray(value.groups)||value.month!==month)throw Error('Spending unavailable');
  const report=value as unknown as CategoryReport;
  try{localStorage.setItem(CATEGORY_KEY+uid+'_'+month,JSON.stringify(report));}catch{/* Device storage is optional assistance. */}
  return report;
 });
 const entry={at:Date.now(),request};categoryReports.set(key,entry);
 if(categoryReports.size>12)categoryReports.delete(categoryReports.keys().next().value!);
 try{return await request;}catch(error){if(categoryReports.get(key)===entry)categoryReports.delete(key);throw error;}
}
